import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, User, Building2, Users } from 'lucide-react';
import { useParams, Outlet, NavLink } from 'react-router-dom';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';

const EntityLayout: React.FC = () => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';

  // Fetch entity details from API
  const entityQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}',
    {
      params: {
        path: {
          organization_id: orgId,
          id: entityId,
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const entity = entityQuery.data;

  const getStatusFlag = (riskLevel: 'low' | 'mid' | 'high' | undefined) => {
    if (!riskLevel) {
      return { 
        label: 'Non évalué', 
        color: 'bg-gray-100 text-gray-800 border-gray-200', 
        icon: User 
      };
    }
    
    if (riskLevel === 'high') {
      return { 
        label: 'Risque élevé', 
        color: 'bg-red-100 text-red-800 border-red-200', 
        icon: XCircle 
      };
    } else if (riskLevel === 'mid') {
      return { 
        label: 'Risque moyen', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        icon: AlertTriangle 
      };
    } else {
      return { 
        label: 'Risque faible', 
        color: 'bg-green-100 text-green-800 border-green-200', 
        icon: CheckCircle 
      };
    }
  };

  const isCompany = entity?.kind === 'company';
  const alertsCount = entity?.alerts_pending || 0;

  if (entityQuery.isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement de l'entité...</span>
        </div>
      </div>
    );
  }

  if (entityQuery.error) {
    return (
      <div className="p-6">
        <ErrorMessage error={entityQuery.error} />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Entité introuvable</h3>
          <p className="text-gray-500">L'entité demandée n'existe pas</p>
        </div>
      </div>
    );
  }

  const statusFlag = getStatusFlag(entity.risk_level);
  const StatusIcon = statusFlag.icon;

  return (
    <div className="p-6">
      {/* Client Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              {isCompany ? (
                <Building2 className="h-6 w-6 text-blue-600" />
              ) : (
                <User className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{entity.name}</h3>
              {entity.email && <p className="text-sm text-gray-500">{entity.email}</p>}
            </div>
          </div>
          <div className="text-right">
            <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-2 ${statusFlag.color}`}>
              <StatusIcon className="h-5 w-5" />
              <span className="font-medium">{statusFlag.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex space-x-6">
        {/* Left Sidebar Navigation */}
        <div className="w-64 space-y-1">
          <NavLink
            to={`/${orgId}/entity/${clientId}`}
            end
            className={({ isActive }) =>
              `w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <User className="h-5 w-5" />
            <span className="font-medium">Identité</span>
          </NavLink>
          <NavLink
            to={`/${orgId}/entity/${clientId}/alerts`}
            className={({ isActive }) =>
              `w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                isActive
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <AlertTriangle className="h-5 w-5" />
            <div className="flex items-center justify-between flex-1">
              <span className="font-medium">Alertes</span>
              {alertsCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {alertsCount}
                </span>
              )}
            </div>
          </NavLink>
          <NavLink
            to={`/${orgId}/entity/${clientId}/structure`}
            className={({ isActive }) =>
              `w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <Users className="h-5 w-5" />
            <span className="font-medium">Structure</span>
          </NavLink>
        </div>

        {/* Content Area */}
        <div className="flex-1 max-w-4xl">
          <Outlet context={{ isCompany, name: entity.name }} />
        </div>
      </div>
    </div>
  );
};

export default EntityLayout;
